/**
 * Which circle alerts this browser has opened, per signed-in customer.
 *
 * The backend's seen state is one `lastSeenAt` per user, which can't express
 * "seen Dad's alerts but not Mom's". This set fills that gap until the
 * per-watcher `/api/v1/notifications` endpoints ship. It is a convenience, not
 * a source of truth: if storage is unavailable or cleared, alerts past
 * `lastSeenAt` simply show as new again.
 *
 * Exposed as a `useSyncExternalStore` source so the header bell and the
 * dashboard badges stay in step.
 */

const EMPTY: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();
let cache: { customerId: number; keys: ReadonlySet<string> } | undefined;

const storageKey = (customerId: number) => `ff.circleAlerts.seen.${customerId}`;

const load = (customerId: number): ReadonlySet<string> => {
  try {
    const raw = window.localStorage.getItem(storageKey(customerId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? new Set(parsed.filter((k): k is string => typeof k === 'string'))
      : EMPTY;
  } catch {
    return EMPTY;
  }
};

export const subscribeSeenAlerts = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Referentially stable between writes, as `useSyncExternalStore` requires. */
export const getSeenAlerts = (
  customerId: number | undefined
): ReadonlySet<string> => {
  if (customerId === undefined || typeof window === 'undefined') {
    return EMPTY;
  }
  if (cache?.customerId !== customerId) {
    cache = { customerId, keys: load(customerId) };
  }
  return cache.keys;
};

export const getServerSeenAlerts = (): ReadonlySet<string> => EMPTY;

export const setSeenAlerts = (
  customerId: number,
  keys: ReadonlySet<string>
) => {
  cache = { customerId, keys };
  try {
    window.localStorage.setItem(
      storageKey(customerId),
      JSON.stringify([...keys])
    );
  } catch {
    // Storage blocked or full — the in-memory set still applies this session.
  }
  for (const listener of listeners) {
    listener();
  }
};
